import { trackItems } from '../analytics/analytics.js';
// src/pages/PackPurchasePayMePage.jsx
// Visual base: prototype L8205-8285 (burgundy) + PaymentInstructionsBlock.
// WP-3: rewired from localStorage to the submitPendingPurchase Cloud Function.
//   - The displayed reference code is sent as `paymentReference` (the code the
//     student quotes; WP-2 uses reference-code + notes, no proof-image upload).
//   - A minimal "which method did you use?" choice + optional notes feed the CF.
//   - On success, the cart item is cleared and we land on /account My Purchases,
//     which now reads /pendingPurchases live from Firestore.
//   - Mock mode has no backend -> gated with a notice (P7).
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReferenceCode } from '../utils/tutorReviewHelpers.js';
import { getIntervalDescription } from '../config/intervalOptions.js';
import PaymentInstructionsBlock from '../components/PaymentInstructionsBlock.jsx';
import LegalConsent from '../components/LegalConsent.jsx';
import PromoCodeBox, { fmtMinorHKD } from '../components/PromoCodeBox.jsx';
import { AUTH_MODE } from '../auth/firebase.js';
import { submitPendingPurchase, recordLegalConsent } from '../lib/callables.js';

const PackPurchasePayMePage = ({ state }) => {
    const navigate = useNavigate();
    const { packId } = useParams();
    const pack = state.packs.find(p => p.id === packId);

    const [pendingRef] = useState(() => 'pp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
    const [method, setMethod] = useState('fps');
    const [notes, setNotes] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [consent, setConsent] = useState({ consented: false, versions: {} });
    // Promo code: { code, preview } from previewPurchase. Display-only; the CF
    // re-derives the price at submit. Seeded from the checkout handoff -- the
    // key is CONSUMED on read (one-shot) and scoped to this pack, so a stale
    // code never auto-applies to a later unrelated purchase.
    const [promo, setPromo] = useState(null);
    const [handoffCode] = useState(() => {
        const raw = sessionStorage.getItem('btl_promo_code');
        sessionStorage.removeItem('btl_promo_code');
        if (!raw) return null;
        try {
            const h = JSON.parse(raw);
            return (h && h.packId === packId && typeof h.code === 'string') ? h.code : null;
        } catch { return null; }
    });

    if (!pack) {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-burgundy-bg paper-texture">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16 text-center">
                    <h1 className="font-display text-burgundy-text text-3xl mb-4">No pack selected</h1>
                    <p className="font-serif italic text-burgundy-muted mb-8">
                        Please choose a pack from your library to continue with payment.
                    </p>
                    <button onClick={() => navigate('/dashboard')}
                        className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-sm hover:bg-burgundy-accentLight transition-colors btn-sweep font-semibold">
                        Browse Packs
                    </button>
                </div>
            </div>
        );
    }

    const refCode = getReferenceCode(pendingRef);
    const isMock = AUTH_MODE === 'mock';
    // The amount the student must actually transfer (discounted when a code is
    // applied; preview totals are minor units, pack.price is HK$ major).
    const payLabel = promo ? fmtMinorHKD(promo.preview.totalAmount) : 'HK$' + pack.price;

    const handleInitiatePayment = async () => {
        setError(null);
        if (isMock) {
            setError('Manual payment submission is unavailable in mock mode. Run with VITE_AUTH_MODE=emulator or firebase.');
            return;
        }
        setProcessing(true);
        try {
            // CF expects each consent as { type, documentVersion } (recordLegalConsent.js).
            const consents = Object.entries(consent.versions).map(([type, documentVersion]) => ({ type, documentVersion }));
            if (consents.length > 0) {
                await recordLegalConsent({ consents, consentMethod: 'checkout' });
            }
            await submitPendingPurchase({
                packIds: [pack.id],
                paymentMethod: method,
                paymentReference: refCode,
                notes: notes.trim() || null,
                ...(promo ? { promoCode: promo.code } : {})
            });
            trackItems('manual_payment_submitted', [pack], { payment_type: method });
            if (state.cart && state.cart.some(c => c.id === pack.id)) {
                state.setCart(state.cart.filter(c => c.id !== pack.id));
            }
            sessionStorage.setItem('btl_account_initial_tab', 'my-purchases');
            navigate('/account');
        } catch (e) {
            // A code disabled between Apply and submit is the one promo failure a
            // student can hit AFTER transferring money -- give a specific way out.
            if (promo && e && e.details && e.details.reason === 'invalid_promo') {
                setError('The code ' + promo.code + ' was just turned off, so this submission was not '
                    + 'recorded. If you already transferred the discounted amount, contact Danielle '
                    + 'quoting reference ' + refCode + ' -- or remove the code to submit at full price.');
            } else {
                setError((e && e.message) ? e.message : 'Could not submit your payment.');
            }
            setProcessing(false);
        }
    };

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-burgundy-bg paper-texture">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <button onClick={() => navigate('/pack-payment-choice/' + packId)}
                    className="font-serif italic text-burgundy-muted text-sm hover:text-burgundy-accent transition-colors mb-6">
                    ← Choose another method
                </button>

                <div className="mb-6">
                    <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">Reference {refCode}</div>
                    <h1 className="font-display text-3xl md:text-4xl text-burgundy-text mb-2 leading-tight">Pay {payLabel} for {pack.label}</h1>
                    {promo && (
                        <p className="font-serif italic text-burgundy-muted mb-1">
                            <span className="line-through">HK${pack.price}</span>
                            {' '}· {promo.code} −{promo.preview.discountPercent}%
                        </p>
                    )}
                    <p className="font-serif italic text-burgundy-muted">{getIntervalDescription(pack.autoRenewInterval)}</p>
                </div>

                {/* Promo code (server-derived preview; the CF re-derives at submit) */}
                <div className="mb-6">
                    <PromoCodeBox theme="burgundy" packIds={[pack.id]} applied={promo}
                        onApplied={setPromo} onRemoved={() => { setPromo(null); setError(null); }}
                        initialCode={handoffCode} />
                </div>

                <div className="border border-burgundy-accent/30 bg-burgundy-page/60 p-6 mb-6">
                    <div className="font-serif text-burgundy-text text-base mb-4">
                        Pay <span className="font-display text-burgundy-accent">{payLabel}</span> using one of the methods below.
                    </div>
                    <PaymentInstructionsBlock
                        booking={state.bookingContent}
                        referenceCode={refCode}
                        prefilledMessage={`Payment for ${pack.label} (${payLabel}), reference ${refCode}`}
                        theme="burgundy"
                    />
                </div>

                {/* Which method + optional notes (the submitPendingPurchase CF needs the method) */}
                <div className="border border-burgundy-accent/30 bg-burgundy-page/60 p-6 mb-6 space-y-4">
                    <div>
                        <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">Which method did you use?</div>
                        <div className="flex gap-2">
                            {[{ id: 'fps', l: 'FPS' }, { id: 'payme', l: 'PayMe' }, { id: 'bank_transfer', l: 'Bank Transfer' }].map(m => (
                                <button key={m.id} type="button" onClick={() => setMethod(m.id)}
                                    className={`px-4 py-2 border font-display text-[12px] transition-colors font-semibold ${method === m.id ? 'border-burgundy-accent bg-burgundy-accent/10 text-burgundy-accent' : 'border-burgundy-accent/30 text-burgundy-muted hover:border-burgundy-accent/60'}`}>
                                    {m.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block font-display text-burgundy-accent text-xs mb-2 font-semibold">Notes for Danielle (optional)</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000}
                            placeholder="e.g. paid from a different name"
                            className="w-full bg-burgundy-pageDark border border-burgundy-accent/30 px-3 py-2.5 text-burgundy-text font-serif focus:outline-none focus:border-burgundy-accent" />
                    </div>
                </div>

                <div className="mb-6">
                    <LegalConsent theme="burgundy"
                        requireTypes={['terms_of_service', 'privacy_policy', 'refund_policy']}
                        onConsentChange={setConsent} />
                </div>

                {error && (
                    <p className="mb-4 font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>
                )}

                <button onClick={handleInitiatePayment} disabled={processing || !consent.consented}
                    className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-sm hover:bg-burgundy-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                    {processing ? 'Submitting…' : 'I have made payment →'}
                </button>

                <p className="font-serif italic text-burgundy-muted text-sm mt-6">
                    Manual processing typically takes 8–12 hours. You'll receive access once Danielle confirms your payment.
                </p>
            </div>
        </div>
    );
};

export default PackPurchasePayMePage;
